import type {ButtonHTMLAttributes, HTMLAttributes, ReactNode} from 'react';

import {cn} from '../../helpers/cn';

export type ChipVariant = 'outline' | 'filled' | 'soft';
export type ChipColor = 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info';
export type ChipSize = 'sm' | 'md';

type ChipCommonProps = {
  label: ReactNode;
  variant?: ChipVariant;
  color?: ChipColor;
  size?: ChipSize;
  selected?: boolean;
  icon?: ReactNode;
  suffix?: ReactNode;
  className?: string;
};

export type ChipProps = ChipCommonProps &
  (
    | ({interactive?: true} & ButtonHTMLAttributes<HTMLButtonElement>)
    | ({interactive?: false; onClick?: never} & HTMLAttributes<HTMLSpanElement>)
  );

const sizeClasses: Record<ChipSize, string> = {
  sm: 'h-8 min-h-8 gap-1.5 px-3 text-[12px] leading-[18px]',
  md: 'h-10 min-h-10 gap-2 px-4 text-[14px] leading-[21px]'
};

const iconSizeClasses: Record<ChipSize, string> = {
  sm: 'text-[18px]',
  md: 'text-[20px]'
};

const outlineBase =
  'border border-disabled-border bg-white text-gray-08 hover:border-gray-08 disabled:cursor-not-allowed disabled:opacity-50';

const outlineSelected = 'border-gray-09 bg-gray-09 text-white hover:border-gray-09';

const variantColorClasses: Record<ChipVariant, Record<ChipColor, string>> = {
  outline: {
    default: outlineBase,
    primary: outlineBase,
    success: outlineBase,
    error: outlineBase,
    warning: outlineBase,
    info: outlineBase
  },
  filled: {
    default: 'border border-transparent bg-gray-02 text-gray-08',
    primary: 'border border-transparent bg-orange text-white',
    success: 'border border-transparent bg-success text-success-on',
    error: 'border border-transparent bg-destructive text-destructive-on',
    warning: 'border border-transparent bg-yellow text-white',
    info: 'border border-transparent bg-blue text-white'
  },
  soft: {
    default: 'border border-transparent bg-gray-02 text-gray-08',
    primary: 'border border-transparent bg-orange-wash text-orange-deep',
    success: 'border border-transparent bg-success-surface text-success-heavy',
    error: 'border border-transparent bg-destructive-surface text-destructive-heavy',
    warning: 'border border-transparent bg-yellow-soft text-yellow-dark-03',
    info: 'border border-transparent bg-blue-mist text-blue-navy'
  }
};

function ChipContent({
  label,
  icon,
  suffix,
  size,
  selected,
  variant
}: Required<Pick<ChipProps, 'label' | 'size' | 'selected' | 'variant'>> &
  Pick<ChipProps, 'icon' | 'suffix'>) {
  return (
    <>
      {icon != null && (
        <span
          aria-hidden
          className={cn(
            'inline-flex shrink-0 text-gray-06',
            iconSizeClasses[size],
            selected && variant === 'outline' && 'text-white'
          )}
        >
          {icon}
        </span>
      )}
      <span className="truncate font-medium">{label}</span>
      {suffix != null && <span className="inline-flex shrink-0 items-center">{suffix}</span>}
    </>
  );
}

export function Chip({
  label,
  variant = 'outline',
  color = 'default',
  size = 'sm',
  selected = false,
  icon,
  suffix,
  className,
  interactive,
  ...props
}: ChipProps) {
  const isInteractive = interactive ?? ('onClick' in props && props.onClick != null);

  const classes = cn(
    'inline-flex max-w-full shrink-0 items-center justify-center rounded-full font-medium font-iransans transition-colors',
    sizeClasses[size],
    selected && variant === 'outline' ? outlineSelected : variantColorClasses[variant][color],
    isInteractive &&
      'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange',
    className
  );

  const contentProps = {label, icon, suffix, size, selected, variant};

  if (!isInteractive) {
    const spanProps = props as HTMLAttributes<HTMLSpanElement>;
    return (
      <span className={classes} {...spanProps}>
        <ChipContent {...contentProps} />
      </span>
    );
  }

  const buttonProps = props as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button {...buttonProps} type={buttonProps.type ?? 'button'} className={classes}>
      <ChipContent {...contentProps} />
    </button>
  );
}
